import {
  isValidElement,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react'
import type { ReactNode } from 'react'
import clsx from 'clsx'
import { convertJsxToString } from '../../../../../shared/component-helper'
import { Flex } from '../../../../../components'
import type { FlexContainerAllProps as FlexContainerProps } from '../../../../../components/flex/Container'
import { Lead } from '../../../../../elements'
import FieldBoundaryProvider from '../../../DataContext/FieldBoundary/FieldBoundaryProvider'
import SectionContainerContext from '../containers/SectionContainerContext'
import Toolbar from '../Toolbar/Toolbar'
import DoneButton from './DoneButton'
import CancelButton from './CancelButton'
import type { SectionContainerProps } from '../containers/SectionContainer'
import SectionContainer from '../containers/SectionContainer'
import type { Path } from '../../../types'
import withComponentMarkers from '../../../../../shared/helpers/withComponentMarkers'

export type FormSectionEditContainerProps = {
  title?: ReactNode
  onDone?: () => void
  onCancel?: () => void
}

export type FormSectionEditContainerAllProps =
  FormSectionEditContainerProps &
    SectionContainerProps &
    FlexContainerProps

function EditContainer(props: FormSectionEditContainerAllProps) {
  const { children, className, title, onDone, onCancel, ...restProps } =
    props || {}
  const ariaLabel = useMemo(() => convertJsxToString(title), [title])
  const {
    containerMode,
    initialContainerMode,
    validateInitially,
    switchContainerMode,
    disableEditing,
  } = useContext(SectionContainerContext) || {}
  const omitFocusManagementRef = useRef(false)

  const onPathError = useCallback(
    (path: Path, error: Error) => {
      if (disableEditing) {
        return
      }
      if (
        initialContainerMode === 'auto' &&
        containerMode !== 'edit' &&
        error instanceof Error
      ) {
        omitFocusManagementRef.current = true
        switchContainerMode?.('edit')
      }
    },
    [
      containerMode,
      disableEditing,
      initialContainerMode,
      switchContainerMode,
    ]
  )

  const childArray = Array.isArray(children) ? children : [children]
  const hasToolbar = childArray.some(
    (child) => isValidElement(child) && child.type === Toolbar
  )

  return (
    <FieldBoundaryProvider
      showErrors={validateInitially}
      onPathError={onPathError}
    >
      <SectionContainer
        mode="edit"
        ariaLabel={ariaLabel}
        omitFocusManagementRef={omitFocusManagementRef}
        className={clsx('dnb-forms-section-edit-block', className)}
        {...restProps}
      >
        <Flex.Stack>
          {title && <Lead size="basis">{title}</Lead>}
          {children}
          {hasToolbar ? null : (
            <Toolbar onDone={onDone} onCancel={onCancel}>
              <DoneButton />
              <CancelButton />
            </Toolbar>
          )}
        </Flex.Stack>
      </SectionContainer>
    </FieldBoundaryProvider>
  )
}

EditContainer.DoneButton = DoneButton
EditContainer.CancelButton = CancelButton

withComponentMarkers(EditContainer, {
  _supportsSpacingProps: true,
})

export default EditContainer
